import {useEffect, useMemo, useState} from 'react';
import md5 from 'md5';
import type {ItemObject} from '@PosterWhiteboard/items/item/item.types';
import {getCurrentPageItems, selectIsCurrentPageModifying} from '@Components/poster-editor/poster-editor-reducer';
import {useAppSelector} from '@/hooks';
import type {RootState} from '@/store';

const MAX_LAYER_THUMB_HEIGHT_PX = 32;
const MAX_LAYER_THUMB_WIDTH_PX = 130;
const RETINA_DPR = 2;
const PROPERTIES_TO_EXCLUDE = new Set(['left', 'top', 'onPageStartTime', 'onPageEndTime', 'timelineSubRow']);

export interface CachedThumbnail {
  url: string;
  hash: string;
}

const itemThumbnailCache = new Map<string, CachedThumbnail>();

export function maxDimensionFor(width: number, height: number): number {
  if (width <= 0 || height <= 0) {
    return 0;
  }
  const heightMult = (MAX_LAYER_THUMB_HEIGHT_PX * RETINA_DPR) / height;
  const widthMult = (MAX_LAYER_THUMB_WIDTH_PX * RETINA_DPR) / width;
  return Math.min(heightMult, widthMult) * Math.max(width, height);
}

function getHashableItemProperties(itemObject: ItemObject): Record<string, unknown> {
  const filtered: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(itemObject)) {
    if (!PROPERTIES_TO_EXCLUDE.has(key)) {
      filtered[key] = value;
    }
  }
  return filtered;
}

export function useItemThumbDataURL(uid: string): string | null {
  const itemObject = useAppSelector((state: RootState): ItemObject | undefined => getCurrentPageItems(state)?.itemsHashMap[uid]);
  const hash = useMemo((): string | null => (itemObject ? md5(JSON.stringify(getHashableItemProperties(itemObject))) : null), [itemObject]);
  const isModifying = useAppSelector(selectIsCurrentPageModifying);

  const [thumb, setThumb] = useState<string | null>(() => itemThumbnailCache.get(uid)?.url ?? null);

  useEffect((): void => {
    if (!hash) {
      setThumb(null);
      return;
    }
    if (isModifying) {
      return;
    }
    const cached = itemThumbnailCache.get(uid);
    if (cached?.hash === hash) {
      setThumb(cached.url);
      return;
    }
    const item = window.posterEditor?.whiteboard?.getCurrentPage()?.items.getItem(uid);
    if (!item?.fabricObject) {
      setThumb(null);
      return;
    }
    try {
      const url = item.toDataURL({maxDimension: maxDimensionFor(item.fabricObject.getScaledWidth(), item.fabricObject.getScaledHeight())});
      itemThumbnailCache.set(uid, {url, hash});
      setThumb(url);
    } catch {
      setThumb(null);
    }
  }, [uid, hash, isModifying]);

  return thumb;
}
